import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, RefreshCw, Search } from 'lucide-react';
import { adminApi } from '../../services/api';
import type { AdminHostAgent, AdminOperationDiagnostic, AdminRelocationDiagnostic } from '../../types/admin';
import { formatHostLoadBytes } from '../../utils/hostLoad';

export function OperationDiagnostics() {
  const [identifier, setIdentifier] = useState('');
  const [result, setResult] = useState<AdminOperationDiagnostic | null>(null);
  const lookup = useMutation({
    mutationFn: (value: string) => adminApi.diagnoseOperation(value),
    onSuccess: setResult,
  });
  const reconcile = useMutation({
    mutationFn: ({ lookupId, operationId }: { lookupId: string; operationId: string }) => (
      adminApi.reconcileOperation(lookupId, operationId)
    ),
    onSuccess: async () => {
      const refreshed = await adminApi.diagnoseOperation(result?.identifier || identifier.trim());
      setResult(refreshed);
    },
  });
  const errorSource = lookup.error || reconcile.error;
  const error = (errorSource as any)?.response?.data?.error || (errorSource as Error | null)?.message;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-100">Lifecycle diagnostics</h2>
        <p className="text-sm text-gray-500">Find persisted lifecycle, checkpoint and relocation state by server, action or operation ID—even after ActionLog expiry.</p>
      </div>
      <form className="card p-4 flex flex-col md:flex-row gap-3" onSubmit={event => {
        event.preventDefault();
        const value = identifier.trim();
        if (value) lookup.mutate(value);
      }}>
        <input className="input flex-1 font-mono text-sm" value={identifier} onChange={event => setIdentifier(event.target.value)} placeholder="Server ID, action ID or operation ID" />
        <button className="btn-primary" disabled={!identifier.trim() || lookup.isPending}><Search className="w-4 h-4" /> {lookup.isPending ? 'Searching…' : 'Diagnose'}</button>
      </form>

      {error && <div className="p-4 border border-red-500/20 bg-red-500/10 rounded-lg text-sm text-red-300">{error}</div>}
      {result && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card p-4 md:col-span-2">
              <div className="text-xs text-gray-500 uppercase mb-2">Server context</div>
              {result.server ? <div className="space-y-2">
                <div className="flex items-center gap-2"><span className={result.server.actualState === 'running' ? 'badge-online' : 'badge-offline'}>{result.server.actualState}</span><span className="text-gray-200 font-medium">{result.server.name}</span><span className="text-gray-500">desired {result.server.desiredState}</span></div>
                <CopyRow label="server" value={result.server.serverId} />
                <CopyRow label="container" value={result.server.containerName} />
                <CopyRow label="action" value={result.server.currentActionId} />
                <CopyRow label="operation" value={result.server.currentOperationId} />
                {result.server.rollbackDrainOwnerStart && <div className="border-l border-yellow-500/40 pl-3 space-y-1">
                  <div className="text-xs text-yellow-300 uppercase">Queued rollback owner start</div>
                  <CopyRow label="queued action" value={result.server.rollbackDrainOwnerStart.actionId} />
                  <CopyRow label="queued operation" value={result.server.rollbackDrainOwnerStart.operationId} />
                  <div className="text-xs text-gray-500">requested {dateLabel(result.server.rollbackDrainOwnerStart.requestedAt)}</div>
                </div>}
                {result.server.stateReason && <p className="text-sm text-gray-400">{result.server.stateReason}</p>}
                {result.server.lifecycleOperation && <pre className="text-xs text-gray-500 bg-dark-900 p-3 rounded overflow-auto">{JSON.stringify(result.server.lifecycleOperation, null, 2)}</pre>}
              </div> : <p className="text-sm text-gray-500">The GameServer was deleted or is no longer retained; ActionLog is still available until TTL expiry.</p>}
            </div>
            <div className="card p-4">
              <div className="text-xs text-gray-500 uppercase mb-2">Host Agent</div>
              {result.hostAgent ? <div className="space-y-2 text-sm"><div className="text-gray-200 font-medium">{result.hostAgent.name}</div><code className="text-gray-500">{result.hostAgent._id}</code><div className="text-gray-400">{result.hostAgent.ipAddress}:{result.hostAgent.port}</div><div className="text-gray-400">health {result.hostAgent.health.status} · socket {result.hostAgent.connection.status}</div></div> : <p className="text-sm text-gray-500">Agent context unavailable.</p>}
            </div>
          </div>

          <MobilityDiagnosticPanel
            result={result}
            retrying={reconcile.isPending}
            onRetry={(operationId) => reconcile.mutate({ lookupId: result.identifier, operationId })}
          />

          <div className="space-y-4">
            {result.actions.map(action => (
              <article key={action._id} className="card overflow-hidden">
                <div className="p-4 border-b border-dark-700 flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex items-center gap-2">{action.status === 'completed' ? <CheckCircle2 className="w-4 h-4 text-neon-400" /> : action.status === 'failed' ? <AlertCircle className="w-4 h-4 text-red-400" /> : <span className="status-dot-online" />}<span className="text-gray-200 font-medium capitalize">{action.type}</span><span className="text-xs text-gray-500">{action.status}</span></div>
                  <div className="text-xs text-gray-500">{new Date(action.startedAt).toLocaleString()}</div>
                </div>
                <div className="p-4 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
                  <div className="space-y-1"><CopyRow label="action" value={action.actionId} /><CopyRow label="operation" value={action.operationId} /><CopyRow label="server" value={String(action.serverId)} />{action.error && <p className="text-sm text-red-400 mt-3">{action.error}</p>}</div>
                  <ol className="space-y-2">
                    {action.entries.map((entry, index) => <li key={`${entry.timestamp}-${index}`} className="text-sm flex gap-3"><span className="text-xs text-gray-600 shrink-0 w-20">{new Date(entry.timestamp).toLocaleTimeString()}</span><span className={entry.level === 'error' ? 'text-red-400' : entry.level === 'warn' ? 'text-yellow-300' : 'text-gray-400'}><span className="text-gray-600">[{entry.source}]</span> {entry.message}</span></li>)}
                  </ol>
                </div>
              </article>
            ))}
            {result.actions.length === 0 && <div className="card p-8 text-center text-gray-500">No retained ActionLogs.</div>}
          </div>
        </>
      )}
    </div>
  );
}

function MobilityDiagnosticPanel({ result, retrying, onRetry }: {
  result: AdminOperationDiagnostic;
  retrying: boolean;
  onRetry: (operationId: string) => void;
}) {
  const { relocation, checkpoint, admission } = result.mobility;
  if (!relocation && !checkpoint && !admission.operation) return null;
  const policy = result.mobilityPolicy.admission;
  return (
    <section className="card p-4 space-y-5">
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
        <div>
          <div className="text-xs text-gray-500 uppercase">GameServer mobility</div>
          <div className="flex flex-wrap gap-2 items-center mt-2">
            <span className={result.mobilityPolicy.enabled ? 'badge-online' : 'badge-offline'}>
              new admission {result.mobilityPolicy.enabled ? 'enabled' : 'disabled'} · rollout {result.mobilityPolicy.rolloutPercent}%
            </span>
            {relocation && <span className="text-sm text-gray-300">serving {relocation.servingPhase}</span>}
            {relocation?.cleanupPhase && <span className="text-sm text-gray-500">cleanup {relocation.cleanupPhase}</span>}
          </div>
        </div>
        {relocation?.retry.eligible && (
          <button
            className="btn-secondary text-xs"
            disabled={retrying}
            onClick={() => confirm(`Schedule bounded reconcile for ${relocation.operationId}?`) && onRetry(relocation.operationId)}
          >
            <RefreshCw className={`w-3 h-3 ${retrying ? 'animate-spin' : ''}`} />
            {retrying ? 'Scheduling…' : `Retry / reconcile (${relocation.attempts.admin}/${relocation.attempts.adminLimit})`}
          </button>
        )}
      </div>

      {relocation && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <PlacementCard title="Source placement" placement={relocation.source} agent={result.placementAgents.source} />
            <PlacementCard title="Target placement" placement={relocation.target} agent={result.placementAgents.target} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DiagnosticBox label="Persisted relocation identity">
              <CopyRow label="relocation" value={relocation.relocationId} />
              <CopyRow label="operation" value={relocation.operationId} />
              <CopyRow label="action" value={relocation.actionId} />
            </DiagnosticBox>
            <DiagnosticBox label="Primitive operation identities">
              <CopyRow label="target prepare" value={relocation.target.prepareOperationId} />
              <CopyRow label="target start" value={relocation.target.startOperationId} />
              <CopyRow label="target stop" value={relocation.target.stopOperationId} />
              <CopyRow label="target compensation" value={relocation.target.compensationOperationId} />
              <CopyRow label="source cleanup" value={relocation.source.cleanupOperationId} />
            </DiagnosticBox>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Metric label="Serving retries" value={relocation.attempts.serving} />
            <Metric label="Cleanup retries" value={relocation.attempts.cleanup} />
            <Metric label="Admin requests" value={`${relocation.attempts.admin}/${relocation.attempts.adminLimit}`} />
            <Metric label="DNS" value={relocation.dns.status} />
            <Metric label="Cleanup remaining" value={relocation.cleanup.remaining || 'none'} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <DiagnosticBox label="Target health">
              <div className={relocation.target.health?.online ? 'text-neon-400' : 'text-gray-300'}>
                {relocation.target.health ? (relocation.target.health.online ? 'online' : 'offline') : 'not observed'}
              </div>
              <div className="text-xs text-gray-500">observed {dateLabel(relocation.target.healthObservedAt)}</div>
              {relocation.target.health?.resources && <div className="text-xs text-gray-500">CPU {relocation.target.health.resources.cpuPercent.toFixed(1)}% · RAM {formatHostLoadBytes(relocation.target.health.resources.memoryUsageBytes)}</div>}
              {relocation.target.health?.error && <div className="text-xs text-red-400">{relocation.target.health.error}</div>}
            </DiagnosticBox>
            <DiagnosticBox label="Rollback target stop">
              <div className={relocation.target.stopProof.exactIdentity && relocation.target.stopProof.observedStopped ? 'text-neon-400' : 'text-gray-300'}>
                {!relocation.target.stopProof.present
                  ? 'proof not observed'
                  : relocation.target.stopProof.exactIdentity && relocation.target.stopProof.observedStopped
                    ? 'exact stopped proof'
                    : 'proof incomplete or mismatched'}
              </div>
              <div className="text-xs text-gray-500">sent {dateLabel(relocation.target.stopSentAt)} · acknowledged {dateLabel(relocation.target.stopAcknowledgedAt)}</div>
              <div className="text-xs text-gray-500">owner lease released {dateLabel(relocation.rollbackOwnerReleasedAt)}</div>
            </DiagnosticBox>
            <DiagnosticBox label="DNS / cleanup">
              <div className="text-gray-300">{relocation.dns.status}</div>
              <div className="text-xs text-gray-500">target recorded {relocation.dns.targetConfigured ? 'yes' : 'no'} · source proof {relocation.cleanup.sourceCleanupConfirmed ? 'yes' : 'no'}</div>
              <div className="text-xs text-gray-500">source capacity released {relocation.cleanup.sourceCapacityReleased ? 'yes' : 'no'}</div>
            </DiagnosticBox>
            <DiagnosticBox label="Retry state">
              <div className={relocation.retry.manualInterventionRequiredAt ? 'text-yellow-300' : 'text-gray-300'}>
                {relocation.retry.manualInterventionRequiredAt ? 'manual intervention marker' : relocation.retry.nextRetryAt ? 'scheduled' : 'not scheduled'}
              </div>
              <div className="text-xs text-gray-500">next {dateLabel(relocation.retry.nextRetryAt)}</div>
              {relocation.retry.lastError && <div className="text-xs text-red-400 break-words">{relocation.retry.lastError}</div>}
            </DiagnosticBox>
          </div>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DiagnosticBox label="Admission decision">
          {admission.decision ? <GateDecision label="Source" decision={admission.decision} /> : <div className="text-sm text-gray-500">No persisted source decision.</div>}
          {admission.targetDecision ? <GateDecision label="Target" decision={admission.targetDecision} /> : <div className="text-xs text-gray-500 mt-2">No persisted target decision.</div>}
          {admission.candidateEvaluations.length > 0 && <div className="mt-3 space-y-2">
            <div className="text-xs text-gray-500 uppercase">Bounded target candidates</div>
            {admission.candidateEvaluations.map(candidate => <div key={`${candidate.agentId}-${candidate.evaluatedAt}`} className="text-xs border-l border-dark-500 pl-2">
              <div className="text-gray-300 font-mono truncate">{candidate.agentId || 'unknown Agent'} · {candidate.decisionSource || 'unknown source'}</div>
              <div><span className={candidate.hard.eligible ? 'text-neon-400' : 'text-red-400'}>hard {candidate.hard.eligible ? 'pass' : 'fail'}</span><span className="text-gray-500"> · {candidate.hard.reason}</span></div>
              {candidate.hard.projected && <HardProjection value={candidate.hard.projected} />}
              <div><span className={candidate.load?.eligible ? 'text-neon-400' : 'text-red-400'}>load {candidate.load ? (candidate.load.eligible ? 'pass' : 'fail') : 'not evaluated'}</span><span className="text-gray-500">{candidate.load?.reason ? ` · ${candidate.load.reason}` : ''}</span></div>
              {candidate.load && <LoadProjection cpu={candidate.load.projectedCpuPercent} available={candidate.load.projectedAvailableBytes} />}
            </div>)}
          </div>}
          <div className="text-xs text-gray-500 mt-3">
            policy CPU ≤ {policy.maxProjectedCpuPercent}% · RAM headroom {formatHostLoadBytes(policy.memoryHeadroomBytes)} · {policy.cpuPressureMinSamples} samples/{Math.round(policy.cpuPressureWindowMs / 1000)}s · fresh {Math.round(policy.freshnessMs / 1000)}s
          </div>
          {relocation && (
            <div className={`text-xs mt-2 ${relocation.target.storageNamespaceCompatible === true ? 'text-neon-400' : 'text-yellow-300'}`}>
              shared namespace {relocation.target.storageNamespaceCompatible === true
                ? 'compatible'
                : relocation.target.storageNamespaceCompatible === false ? 'not compatible' : 'compatibility unknown'}
            </div>
          )}
        </DiagnosticBox>
        <DiagnosticBox label="Internal checkpoint status (descriptor redacted)">
          {checkpoint ? <>
            <div className="flex flex-wrap gap-2 items-center text-sm">
              <span className={checkpoint.freshForServerData ? 'badge-online' : 'badge-offline'}>{checkpoint.freshForServerData ? 'fresh' : 'not fresh'}</span>
              <span className="text-gray-300">{checkpoint.status} / {checkpoint.phase}</span>
            </div>
            <div className="text-xs text-gray-500 mt-2">server generation {result.server?.dataGeneration ?? '—'} · record {checkpoint.dataGeneration} · current {checkpoint.current?.dataGeneration ?? '—'}</div>
            <div className="text-xs text-gray-500">verified {dateLabel(checkpoint.current?.verifiedAt || null)} · attempts {checkpoint.attempts}</div>
            {checkpoint.lastError && <div className="text-xs text-red-400 mt-1">{checkpoint.lastError}</div>}
          </> : <div className="text-sm text-gray-500">No persisted checkpoint record.</div>}
        </DiagnosticBox>
      </div>
    </section>
  );
}

function PlacementCard({ title, placement, agent }: {
  title: string;
  placement: AdminRelocationDiagnostic['source'] | AdminRelocationDiagnostic['target'];
  agent: AdminHostAgent | null;
}) {
  return <DiagnosticBox label={title}>
    <div className="flex items-center gap-2"><span className="text-gray-200">{agent?.name || 'Agent unavailable'}</span>{agent && <span className={agent.mobility.fullReady ? 'badge-online' : 'badge-offline'}>{agent.mobility.fullReady ? 'full' : 'incomplete'}</span>}</div>
    <CopyRow label="agent" value={placement.agentId} />
    <CopyRow label="reservation" value={placement.reservationId} />
    <div className="text-xs text-gray-500">revision {placement.placementRevision ?? '—'} · weight {placement.weight ?? '—'} · port {placement.basePort ?? '—'}</div>
    {'dataGeneration' in placement && <div className="text-xs text-gray-500">data generation {placement.dataGeneration ?? '—'} · cleanup proof {placement.cleanupConfirmed ? 'yes' : 'no'}</div>}
    {'attemptGeneration' in placement && <div className="text-xs text-gray-500">attempt generation {placement.attemptGeneration ?? '—'} · prepared {placement.prepared ? dateLabel(placement.prepared.preparedAt) : 'no'}</div>}
  </DiagnosticBox>;
}

function GateDecision({ label, decision }: {
  label: string;
  decision: NonNullable<AdminOperationDiagnostic['mobility']['admission']['decision']>
    | NonNullable<AdminOperationDiagnostic['mobility']['admission']['targetDecision']>;
}) {
  return <div className="mt-2">
    <div className="text-xs text-gray-500">{label} · policy {decision.matchesCurrentPolicy ? 'current' : 'superseded'}{'decisionSource' in decision && decision.decisionSource ? ` · ${decision.decisionSource}` : ''}</div>
    <div className="text-sm"><span className={decision.hard.eligible ? 'text-neon-400' : 'text-red-400'}>hard {decision.hard.eligible ? 'pass' : 'fail'}</span><span className="text-gray-500"> · {decision.hard.reason}</span></div>
    {'projected' in decision.hard && decision.hard.projected && <HardProjection value={decision.hard.projected} />}
    <div className="text-sm"><span className={decision.load.eligible ? 'text-neon-400' : 'text-red-400'}>load {decision.load.eligible ? 'pass' : 'fail'}</span><span className="text-gray-500"> · {decision.load.reason}</span></div>
    {'projectedCpuPercent' in decision.load && <LoadProjection cpu={decision.load.projectedCpuPercent} available={decision.load.projectedAvailableBytes} />}
  </div>;
}

function HardProjection({ value }: {
  value: { weight: number; nanoCpus: number; memoryBytes: number; storageBytes: number; packageCount: number };
}) {
  return <div className="text-xs text-gray-500">
    projected weight {value.weight} · CPU {(value.nanoCpus / 1_000_000_000).toFixed(2)} cores · RAM {formatHostLoadBytes(value.memoryBytes)} · storage {formatHostLoadBytes(value.storageBytes)} · package count {value.packageCount}
  </div>;
}

function LoadProjection({ cpu, available }: { cpu: number | null; available: number | null }) {
  if (cpu === null && available === null) return null;
  return <div className="text-xs text-gray-500">
    projected {cpu === null ? 'CPU —' : `CPU ${cpu.toFixed(1)}%`} · available RAM {available === null ? '—' : formatSignedBytes(available)}
  </div>;
}

function formatSignedBytes(value: number): string {
  return value < 0 ? `-${formatHostLoadBytes(Math.abs(value))}` : formatHostLoadBytes(value);
}

function DiagnosticBox({ label, children }: { label: string; children: ReactNode }) {
  return <div className="p-3 bg-dark-700 rounded"><div className="text-xs text-gray-500 uppercase mb-2">{label}</div>{children}</div>;
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return <div className="p-3 bg-dark-700 rounded"><div className="text-xs text-gray-500">{label}</div><div className="text-sm font-medium text-gray-200 break-words">{value}</div></div>;
}

function dateLabel(value: string | null | undefined) {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed.toLocaleString() : '—';
}

function CopyRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return <button className="block max-w-full text-left text-xs text-gray-500 hover:text-gray-300 font-mono truncate" title={`Copy ${label} ID`} onClick={() => navigator.clipboard?.writeText(value)}><span className="text-gray-600 font-sans">{label}: </span>{value}</button>;
}
